import { useCallback, useRef } from 'react';
import * as Cesium from 'cesium';

export type HeatmapMetricMode = 'max' | 'avg' | 'min' | 'count' | 'duration' | 'std';

export interface CoveragePassItem {
  pass_id?: string;
  spot_id?: string;
  id?: string;
  start_ts?: number;
  end_ts?: number;
  startTimeSec?: number;
  endTimeSec?: number;
  lla?: {
    lat?: number;
    lng?: number;
    lon?: number;
    alt?: number;
  };
  lat?: number;
  lng?: number;
  lon?: number;
  gap_intervals?: [number, number][];
  revisit_count?: number;
  revisit_gap_status?: string;
  max_gap_seconds?: number | null;
  avg_gap_seconds?: number | null;
  min_gap_seconds?: number | null;
  std_gap_seconds?: number | null;
  mission_max_gap_seconds?: number | null;
  mission_avg_gap_seconds?: number | null;
  mission_min_gap_seconds?: number | null;
  mission_std_gap_seconds?: number | null;
  geometry?: {
    type?: string;
    coordinates?: any;
  };
}

export function useCoverageVisualization(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
) {
  const coverageEntitiesRef = useRef<Map<string, Cesium.Entity>>(new Map());
  const pointCollectionRef = useRef<Cesium.PointPrimitiveCollection | null>(null);

  const clearCoverageVisualization = useCallback(() => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;

    if (pointCollectionRef.current && v && !v.isDestroyed()) {
      v.scene.primitives.remove(pointCollectionRef.current);
      pointCollectionRef.current = null;
    }

    coverageEntitiesRef.current.forEach(entity => {
      if (ds) ds.entities.remove(entity);
      else if (v) v.entities.remove(entity);
    });
    coverageEntitiesRef.current.clear();
    v?.scene.requestRender();
  }, [viewerRef, sharedDataSourceRef]);

  const renderCoveragePasses = useCallback((
    passes: CoveragePassItem[],
    isGap: boolean = false,
    metricMode: HeatmapMetricMode = 'max',
    totalDurationMin?: number,
    customThresholds?: number[]
  ) => {
    clearCoverageVisualization();
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !passes || !Array.isArray(passes) || passes.length === 0) return;

    const targetDS = ds || v;

    const hasPoints = passes.some(pass => {
      const latVal = Number(pass.lla?.lat ?? pass.lat);
      const lngVal = Number(pass.lla?.lng ?? pass.lng ?? pass.lla?.lon ?? pass.lon);
      return Number.isFinite(latVal) && Number.isFinite(lngVal) && (!pass.geometry?.coordinates || pass.geometry.coordinates.length === 0);
    });

    if (hasPoints) {
      pointCollectionRef.current = new Cesium.PointPrimitiveCollection();
      v.scene.primitives.add(pointCollectionRef.current);
    }

    // Calculate dynamic range bounds across all items for dynamic duration colorization
    const getItemVal = (p: CoveragePassItem | any): number => {
      if (metricMode === 'count') {
        const val = p.revisit_count ?? p.revisitCount ?? p.revisits ?? p.count;
        return val != null ? Number(val) : 0;
      }
      if (metricMode === 'duration' || metricMode === 'max') {
        const val = p.max_gap_seconds ?? p.maxGapSeconds ?? p.max_gap ?? p.maxGap ??
                    p.mission_max_gap_seconds ?? p.missionMaxGapSeconds ?? p.mission_max_gap ?? p.missionMaxGap;
        return val != null ? Number(val) : 0;
      }
      if (metricMode === 'avg') {
        const val = p.avg_gap_seconds ?? p.avgGapSeconds ?? p.avg_gap ?? p.avgGap ??
                    p.mission_avg_gap_seconds ?? p.missionAvgGapSeconds ?? p.mission_avg_gap ?? p.missionAvgGap;
        return val != null ? Number(val) : 0;
      }
      if (metricMode === 'min') {
        const val = p.min_gap_seconds ?? p.minGapSeconds ?? p.min_gap ?? p.minGap ??
                    p.mission_min_gap_seconds ?? p.missionMinGapSeconds ?? p.mission_min_gap ?? p.missionMinGap;
        return val != null ? Number(val) : 0;
      }
      if (metricMode === 'std') {
        const val = p.std_gap_seconds ?? p.stdGapSeconds ?? p.std_gap ?? p.stdGap ??
                    p.mission_std_gap_seconds ?? p.missionStdGapSeconds ?? p.mission_std_gap ?? p.missionStdGap;
        return val != null ? Number(val) : 0;
      }
      return 0;
    };

    let minVal = Infinity;
    let maxVal = -Infinity;

    passes.forEach((p) => {
      const val = getItemVal(p);
      if (typeof val === 'number' && Number.isFinite(val)) {
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
      }
    });

    if (!Number.isFinite(minVal)) minVal = 0;
    if (!Number.isFinite(maxVal) || maxVal <= minVal) maxVal = minVal + 300;

    const step = (maxVal - minVal) / 5;
    const b1 = minVal + step;
    const b2 = minVal + 2 * step;
    const b3 = minVal + 3 * step;
    const b4 = minVal + 4 * step;

    const getDynamicSpotColor = (p: CoveragePassItem): Cesium.Color => {
      // 1. Check revisit_gap_status first. If it is insufficient_revisits, show RED.
      const status = p.revisit_gap_status || (p as any).revisitGapStatus || (p as any).status;
      if (status === 'insufficient_revisits') {
        return Cesium.Color.fromCssColorString('#ef4444').withAlpha(0.95);
      }

      const val = getItemVal(p);

      // Invert color scale logic:
      // Larger is BAD (Red) if isGap (or revisit duration)
      // Larger is GOOD (Green) if isCount
      const isCount = metricMode === 'count';
      const isLargerBad = isGap && !isCount;

      const colors = isLargerBad
        ? [
            '#22c55e', // Green (small is Good)
            '#84cc16',
            '#eab308',
            '#f97316',
            '#ef4444', // Red (large is Bad)
          ]
        : [
            '#ef4444', // Red (small is Bad)
            '#f97316',
            '#eab308',
            '#84cc16',
            '#22c55e', // Green (large is Good)
          ];

      if (customThresholds && customThresholds.length === 4) {
        const [t1, t2, t3, t4] = customThresholds;
        if (val < t1) return Cesium.Color.fromCssColorString(colors[0]).withAlpha(0.85);
        if (val < t2) return Cesium.Color.fromCssColorString(colors[1]).withAlpha(0.85);
        if (val < t3) return Cesium.Color.fromCssColorString(colors[2]).withAlpha(0.85);
        if (val < t4) return Cesium.Color.fromCssColorString(colors[3]).withAlpha(0.85);
        return Cesium.Color.fromCssColorString(colors[4]).withAlpha(0.85);
      }

      if (metricMode === 'count') {
        // Auto-bin count based on max count if no custom thresholds
        let maxCount = 5;
        passes.forEach(item => {
          const c = item.revisit_count ?? 0;
          if (c > maxCount) maxCount = c;
        });
        const stepCount = maxCount / 5;
        if (val < stepCount) return Cesium.Color.fromCssColorString(colors[0]).withAlpha(0.85);
        if (val < 2 * stepCount) return Cesium.Color.fromCssColorString(colors[1]).withAlpha(0.85);
        if (val < 3 * stepCount) return Cesium.Color.fromCssColorString(colors[2]).withAlpha(0.85);
        if (val < 4 * stepCount) return Cesium.Color.fromCssColorString(colors[3]).withAlpha(0.85);
        return Cesium.Color.fromCssColorString(colors[4]).withAlpha(0.85);
      }

      if (totalDurationMin && totalDurationMin > 0) {
        const totalDurationSec = totalDurationMin * 60;
        const stepSec = totalDurationSec / 5;
        
        if (val < stepSec) return Cesium.Color.fromCssColorString(colors[0]).withAlpha(0.85);
        if (val < 2 * stepSec) return Cesium.Color.fromCssColorString(colors[1]).withAlpha(0.85);
        if (val < 3 * stepSec) return Cesium.Color.fromCssColorString(colors[2]).withAlpha(0.85);
        if (val < 4 * stepSec) return Cesium.Color.fromCssColorString(colors[3]).withAlpha(0.85);
        return Cesium.Color.fromCssColorString(colors[4]).withAlpha(0.85);
      }

      if (val < b1) return Cesium.Color.fromCssColorString(colors[0]).withAlpha(0.95);
      if (val < b2) return Cesium.Color.fromCssColorString(colors[1]).withAlpha(0.95);
      if (val < b3) return Cesium.Color.fromCssColorString(colors[2]).withAlpha(0.95);
      if (val < b4) return Cesium.Color.fromCssColorString(colors[3]).withAlpha(0.95);
      return Cesium.Color.fromCssColorString(colors[4]).withAlpha(0.95);
    };

    passes.forEach((pass, index) => {
      const passId = pass.pass_id || pass.spot_id || pass.id || `pass-${index}`;
      const startTs = pass.start_ts ?? pass.startTimeSec ?? 0;
      const endTs = pass.end_ts ?? pass.endTimeSec ?? 0;

      const baseColor = isGap
        ? Cesium.Color.fromCssColorString('#ff4d4d')
        : Cesium.Color.fromCssColorString('#00d2ff');

      const activeColor = isGap
        ? Cesium.Color.fromCssColorString('#ff2222')
        : Cesium.Color.fromCssColorString('#ccf54e');

      // 1. Handle Point Spot (lla: { lat, lng })
      const latVal = Number(pass.lla?.lat ?? pass.lat);
      const lngVal = Number(pass.lla?.lng ?? pass.lng ?? pass.lla?.lon ?? pass.lon);

      if (Number.isFinite(latVal) && Number.isFinite(lngVal) && (!pass.geometry?.coordinates || pass.geometry.coordinates.length === 0)) {
        const rawAlt = Number(pass.lla?.alt ?? 50);
        const altVal = Number.isFinite(rawAlt) && rawAlt > 0 ? rawAlt : 50;
        const position = Cesium.Cartesian3.fromDegrees(lngVal, latVal, altVal);
        const spotColor = getDynamicSpotColor(pass);

        if (pointCollectionRef.current) {
          pointCollectionRef.current.add({
            id: `coverage-spot-${passId}`,
            position,
            color: spotColor,
            pixelSize: 4,
            outlineColor: Cesium.Color.BLACK.withAlpha(0.5),
            outlineWidth: 0.5,
          });
        }
        return;
      }

      // 2. Handle Polygon Geometry (coordinates)
      const rawCoords = pass.geometry?.coordinates;
      if (!rawCoords || !Array.isArray(rawCoords) || rawCoords.length === 0) return;

      const outerRing = Array.isArray(rawCoords[0]) ? rawCoords[0] : rawCoords;
      const flatCoords: number[] = [];

      outerRing.forEach((point: any) => {
        if (Array.isArray(point) && point.length >= 2) {
          const lon = Number(point[0]);
          const lat = Number(point[1]);
          if (Number.isFinite(lon) && Number.isFinite(lat)) {
            flatCoords.push(lon, lat);
          }
        }
      });

      if (flatCoords.length < 6) return;

      const positions = Cesium.Cartesian3.fromDegreesArray(flatCoords);

      const colorCallback = new Cesium.CallbackProperty(() => {
        if (!v || !v.clock) return baseColor.withAlpha(0.30);

        const clockTime = v.clock.currentTime;
        const currentSec = Cesium.JulianDate.toDate(clockTime).getTime() / 1000;

        if (startTs > 0 && endTs > 0) {
          if (currentSec >= startTs && currentSec <= endTs) {
            return activeColor.withAlpha(0.65);
          }
        }

        return baseColor.withAlpha(0.30);
      }, false);

      const materialProperty = new Cesium.ColorMaterialProperty(colorCallback as any);

      const outlineColorProperty = new Cesium.CallbackProperty(() => {
        if (!v || !v.clock) return baseColor.withAlpha(0.7);

        const clockTime = v.clock.currentTime;
        const currentSec = Cesium.JulianDate.toDate(clockTime).getTime() / 1000;

        if (startTs > 0 && endTs > 0 && currentSec >= startTs && currentSec <= endTs) {
          return activeColor.withAlpha(1.0);
        }

        return baseColor.withAlpha(0.7);
      }, false);

      const entity = targetDS.entities.add({
        id: `coverage-pass-${passId}`,
        name: `Pass ${passId}`,
        polygon: {
          hierarchy: positions,
          material: materialProperty,
          outline: false,
          classificationType: Cesium.ClassificationType.TERRAIN,
          arcType: Cesium.ArcType.GEODESIC,
        },
      });

      coverageEntitiesRef.current.set(passId, entity);
    });

    v.scene.requestRender();
  }, [viewerRef, sharedDataSourceRef, clearCoverageVisualization]);

  return {
    renderCoveragePasses,
    clearCoverageVisualization,
  };
}
